// Tổng quan thẻ liệu trình: số dư phải phục vụ, công nợ, sử dụng theo tháng, danh sách cần xử lý
import React, { useMemo, useState } from 'react';
import { Empty } from 'antd';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { formatPhone } from '../POS/utils';
import type { TreatmentCard } from './data';
import { cardBalance, cardFlags, consumedAmount, monthlyUsage, outstanding, paidAmount, type CardFlags } from './logic';
import useTreatmentStore from './store';
import { SellCardModal } from './modals';
import CardDetail from './CardDetail';
import TreatmentShell, { BalanceCell, Kpi, fmtDate, money, moneyShort } from './ui';
import { UnsignedVisits } from '../Customer360/VisitWidgets';

type Row = { c: TreatmentCard; f: CardFlags };

function AlertList({ title, hint, rows, more, onOpen, render }: { title: string; hint: string; rows: Row[]; more: () => void; onOpen: (id: string) => void; render: (r: Row) => React.ReactNode }) {
  return (
    <div className="flex flex-col rounded-xl border border-slate-200 bg-white">
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
        <div><span className="font-semibold">{title}</span> <span className="text-sm text-slate-500">({rows.length})</span><div className="text-xs text-slate-500">{hint}</div></div>
        {rows.length > 5 && <button onClick={more} className="text-xs font-semibold text-[#2c6b7f] hover:underline">Xem tất cả</button>}
      </div>
      {rows.length === 0 ? <Empty className="!my-6" image={Empty.PRESENTED_IMAGE_SIMPLE} description="Không có" /> : (
        <div className="divide-y divide-slate-100">
          {rows.slice(0, 5).map(r => (
            <button key={r.c.id} onClick={() => onOpen(r.c.id)} className="flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-sm hover:bg-slate-50">
              <span className="min-w-0"><b>{r.c.customerName}</b> <span className="text-xs text-slate-500">{formatPhone(r.c.customerPhone)}</span><div className="truncate text-xs text-slate-500">{r.c.packageName}</div></span>
              <span className="shrink-0 text-right">{render(r)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function TreatmentDashboard() {
  const cards = useTreatmentStore(s => s.cards);
  const cfg = useTreatmentStore(s => s.config);
  const navigate = useNavigate();
  const [detailId, setDetailId] = useState<string | null>(null);
  const [selling, setSelling] = useState(false);

  const rows: Row[] = useMemo(() => cards.map(c => ({ c, f: cardFlags(c, cfg) })), [cards, cfg]);
  const active = rows.filter(r => r.f.status === 'active');
  const months = useMemo(() => monthlyUsage(cards, 12), [cards]);
  const thisMonth = months[months.length - 1];
  const monthStart = dayjs().startOf('month').toISOString();
  const soldMonth = cards.filter(c => c.createdAt >= monthStart && c.source === 'pos');
  const remainingSessions = active.reduce((s, r) => s + (r.c.kind === 'session' ? cardBalance(r.c).remainingSessions : 0), 0);
  const unusedPaid = active.reduce((s, r) => s + Math.max(0, paidAmount(r.c) - consumedAmount(r.c)), 0);
  const debt = rows.reduce((s, r) => s + outstanding(r.c), 0);
  const overdue = rows.filter(r => r.f.overdue);
  const today = dayjs().format('YYYY-MM-DD');
  const todayBookings = cards.reduce((s, c) => s + c.bookings.filter(b => b.status === 'booked' && dayjs(b.at).format('YYYY-MM-DD') === today).length, 0);

  const low = active.filter(r => r.f.low).sort((a, b) => a.f.daysToExpire - b.f.daysToExpire);
  const expiring = rows.filter(r => r.f.expiring || r.f.status === 'expired').sort((a, b) => a.f.daysToExpire - b.f.daysToExpire);
  const overdueSorted = [...overdue].sort((a, b) => b.f.maxOverdueDays - a.f.maxOverdueDays);

  const byPackage = useMemo(() => {
    const m = new Map<string, { name: string; sold: number; active: number; revenue: number }>();
    rows.forEach(({ c, f }) => {
      const x = m.get(c.packageId) || { name: c.packageName, sold: 0, active: 0, revenue: 0 };
      x.sold += 1; x.revenue += c.status === 'cancelled' ? 0 : c.price;
      if (f.status === 'active') x.active += 1;
      m.set(c.packageId, x);
    });
    return Array.from(m.values()).sort((a, b) => b.revenue - a.revenue);
  }, [rows]);

  return (
    <TreatmentShell title="Tổng quan Nội khoa" subtitle={`Cập nhật ${dayjs().format('HH:mm DD/MM/YYYY')} – số liệu thay đổi ngay khi trừ buổi, thu tiền, hoàn tiền.`} onSell={() => setSelling(true)}>
      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Kpi label="Thẻ đang dùng" value={active.length} sub={`${rows.filter(r => r.f.status === 'expired').length} thẻ hết hạn còn buổi`} onClick={() => navigate('/treatment/cards?status=active')} />
        <Kpi label="Buổi còn phải phục vụ" value={remainingSessions} sub={`Tiền khách trả trước chưa dùng ${moneyShort(unusedPaid)}`} />
        <Kpi label="Công nợ thẻ" tone={overdue.length ? 'danger' : 'default'} value={moneyShort(debt)} sub={overdue.length ? `${overdue.length} thẻ quá hạn` : 'Không có nợ quá hạn'} onClick={() => navigate('/treatment/debts')} />
        <Kpi label={`Sử dụng tháng ${dayjs().format('MM')}`} value={thisMonth.visits} sub={`${thisMonth.sessions} buổi · ghi nhận ${moneyShort(thisMonth.value)}`} />
        <Kpi label="Lịch hẹn hôm nay" tone="ok" value={todayBookings} sub={`${soldMonth.length} thẻ bán mới tháng này`} onClick={() => navigate('/treatment/checkin')} />
      </div>

      <div className="mb-4 grid gap-4 lg:grid-cols-[1fr_360px]">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="mb-2 font-semibold">Sử dụng thẻ 12 tháng</div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={months} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" fontSize={11} />
                <YAxis yAxisId="l" allowDecimals={false} fontSize={11} />
                <YAxis yAxisId="r" orientation="right" fontSize={11} tickFormatter={v => moneyShort(v)} />
                <Tooltip formatter={(v: number, name: string) => (name === 'Giá trị ghi nhận' ? [`${money(v)}đ`, name] : [v, name])} />
                <Legend />
                <Bar yAxisId="l" dataKey="visits" name="Lượt đến" fill="#3e91ab" radius={[4, 4, 0, 0]} />
                <Bar yAxisId="l" dataKey="sessions" name="Buổi trừ" fill="#a5d3e0" radius={[4, 4, 0, 0]} />
                <Line yAxisId="r" dataKey="value" name="Giá trị ghi nhận" stroke="#f59e0b" strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white">
          <div className="border-b border-slate-100 px-4 py-2.5 font-semibold">Theo gói</div>
          <div className="divide-y divide-slate-100 text-sm">
            {byPackage.map(p => (
              <div key={p.name} className="flex items-center justify-between gap-2 px-4 py-2">
                <span className="min-w-0"><span className="block truncate">{p.name}</span><span className="text-xs text-slate-500">{p.sold} thẻ · {p.active} đang dùng</span></span>
                <b className="shrink-0">{moneyShort(p.revenue)}</b>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <AlertList title="Sắp hết thẻ" hint={`Còn ≤ ${cfg.lowSessions} buổi hoặc ≤ ${cfg.lowValuePct}% số dư – tư vấn mua tiếp`} rows={low} more={() => navigate('/treatment/cards?warn=low')} onOpen={setDetailId}
          render={r => <BalanceCell card={r.c} />} />
        <AlertList title="Sắp hết hạn / đã hết hạn" hint={`Hết hạn trong ${cfg.expiringDays} ngày – nhắc khách đến làm hoặc gia hạn`} rows={expiring} more={() => navigate('/treatment/cards?warn=expiring')} onOpen={setDetailId}
          render={r => <span className={`text-xs font-semibold ${r.f.daysToExpire < 0 ? 'text-orange-600' : 'text-amber-700'}`}>{fmtDate(r.c.expiresAt)}<div className="font-normal text-slate-500">{r.f.daysToExpire < 0 ? `quá ${-r.f.daysToExpire} ngày` : `còn ${r.f.daysToExpire} ngày`}</div></span>} />
        <AlertList title="Nợ quá hạn" hint="Đợt thanh toán đã qua ngày hẹn – gọi nhắc khách" rows={overdueSorted} more={() => navigate('/treatment/debts')} onOpen={setDetailId}
          render={r => <span className="text-xs"><b className="text-red-600">{money(r.f.overdueAmount)}đ</b><div className="text-slate-500">trễ {r.f.maxOverdueDays} ngày</div></span>} />
      </div>

      <div className="mt-4"><UnsignedVisits /></div>

      <CardDetail cardId={detailId} onClose={() => setDetailId(null)} />
      <SellCardModal open={selling} onClose={() => setSelling(false)} onDone={c => setDetailId(c.id)} />
    </TreatmentShell>
  );
}
